<template>
    <Transition name="fade" mode="out-in">
        <div v-if="loading" class="px-4 py-6 lg:px-0">
            <MiscSkeleton
                color="bg-base-300"
                size="medium"
                :repeat="13"
            />
        </div>
        <div v-else-if="!loading">
            <WishlistProductList
                :key="refreshWishlist"
                :products="wishlistProducts"
                :excentos-configurations="excentosConfigurations"
            />
        </div>
    </Transition>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { ApiClientError, type ApiError } from '@shopware/api-client'
import { wishlistIncludes } from '~/utils/includes/product'

const nuxtApp = useNuxtApp()
const { getWishlistProducts, items, limit, canSyncWishlist } = useWishlist()
const { excentosConfigurations: syncedExcentosConfigurations } = useSyncWishlist()
const { configs: localExcentosConfigurations } = useLocalWishlist()
const excentosConfigurations = computed(() =>
    canSyncWishlist.value ? syncedExcentosConfigurations.value : localExcentosConfigurations.value,
)
const { showNotification } = useNotification()
const { apiClient } = useShopwareContext()
const wishlistProducts = ref<Schemas["Product"][]>([])
const refreshWishlist = ref(0)

// Footer lock functionality to prevent layout shifts during loading
const { lockFooter, unlockFooter } = useFooterLock()

// Set loading state to prevent flash old wishlist state before onMounted hook is finished
const loading = ref(true)

// Prevents the watch from triggering a duplicate fetch during the initial load.
// onMounted handles the first fetch directly; the watch handles subsequent changes.
let initialLoadComplete = false

// Watch for changes in wishlist items after initial load
// (e.g. user adds/removes items while on the wishlist page).
// Compares IDs as a string to avoid refiring when getWishlistProducts()
// is called from app.vue and replaces the array with the same contents.
watch(
    () => items.value.join(','),
    async (newIds, oldIds) => {
        if (!initialLoadComplete) return
        if (newIds === oldIds) return
        const newItems = newIds ? newIds.split(',') : []
        try {
            await fetchWishlistProducts(newItems)
            refreshWishlist.value++
        } catch (e) {
            console.warn('Could not refresh wishlist products:', e)
        }
    }
)

onMounted(async () => {
    // Lock footer to prevent layout shifts during wishlist loading
    lockFooter('wishlist-loading', 'Loading wishlist items')

    try {
        // Sync wishlist IDs from backend
        await getWishlistProducts({ limit: 50 })

        // Fetch full product data directly — this handles both hard refresh
        // (items just populated) and client-side navigation (items already set,
        // so the watch above would not have fired).
        await fetchWishlistProducts(items.value)
        initialLoadComplete = true

        if (wishlistProducts.value.length > 0) {
            nuxtApp.callHook('viewWishlist', { wishlist: wishlistProducts.value })
        }
    } finally {
        setTimeout(() => {
            unlockFooter('wishlist-loading')
        }, 300)
    }
})

async function fetchWishlistProducts (ids: string[]) {
    if (ids.length === 0) {
        wishlistProducts.value = []
        loading.value = false
        return
    }

    try {
        loading.value = true 
        const { data } = await apiClient.invoke('readProduct post /product', {
            headers: { 'sw-include-seo-urls': true },
            body: {
                filter: [{ type: 'equalsAny', field: 'id', value: ids.join('|') }],
                limit: limit.value,
                includes: wishlistIncludes,
                associations: {
                    media: {
                        associations: {
                            media: {}
                        }
                    },
                    cover: { associations: { media: {} } },
                    options: { associations: { group: {} } }
                }
            }
        })
        const elements = data.elements || []
        // Preserve the wishlist order (API returns products in arbitrary order)
        wishlistProducts.value = ids
            .map(id => elements.find(p => p.id === id))
            .filter((p): p is Schemas['Product'] => p !== undefined)
    } catch (e: unknown) {
        if (e instanceof ApiClientError) {
            e.details?.errors.forEach((error: ApiError) => {
                showNotification(error?.detail ?? '', 'error', true)
            })
        }
    } finally {
        loading.value = false
    }
}
</script>
